import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Phone, 
  MessageSquare, 
  Mail, 
  Users, 
  Megaphone, 
  TrendingUp,
  ArrowRight,
  AlertCircle
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const fallbackData = [
    { name: 'SMS', value: stats?.total_sms || 0, color: '#0F172A' },
    { name: 'Email', value: stats?.total_emails || 0, color: '#64748B' },
    { name: 'WhatsApp', value: stats?.total_whatsapp || 0, color: '#F97316' },
  ];

  const channelData = [
    { name: 'Sesli Arama', success: stats?.successful_calls || 0, total: stats?.total_calls || 0 },
    { name: 'SMS', success: stats?.total_sms || 0, total: stats?.total_sms || 0 },
    { name: 'Email', success: stats?.total_emails || 0, total: stats?.total_emails || 0 },
    { name: 'WhatsApp', success: stats?.total_whatsapp || 0, total: stats?.total_whatsapp || 0 },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-hover" data-testid="stat-contacts">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Toplam Kişi</p>
                <p className="text-3xl font-bold mt-1">{stats?.total_contacts || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center">
                <Users className="w-6 h-6 text-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-campaigns">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Toplam Kampanya</p>
                <p className="text-3xl font-bold mt-1">{stats?.total_campaigns || 0}</p>
                {stats?.active_campaigns > 0 && (
                  <Badge variant="outline" className="mt-2 bg-emerald-100 text-emerald-800 border-emerald-200">
                    {stats.active_campaigns} Aktif
                  </Badge>
                )}
              </div>
              <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center">
                <Megaphone className="w-6 h-6 text-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-calls">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Başarılı Arama</p>
                <p className="text-3xl font-bold mt-1">{stats?.successful_calls || 0}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  %{stats?.call_success_rate || 0} başarı oranı
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Phone className="w-6 h-6 text-emerald-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-fallbacks">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Fallback Mesaj</p>
                <p className="text-3xl font-bold mt-1">{stats?.total_fallbacks || 0}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  SMS + Email + WhatsApp
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#F97316]/10 flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-[#F97316]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Fallback Distribution */}
        <Card className="lg:col-span-4" data-testid="fallback-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Fallback Dağılımı</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.total_fallbacks > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={fallbackData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {fallbackData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-4">
                  {fallbackData.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                      <span className="text-sm text-muted-foreground">{item.name}: {item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Henüz fallback verisi yok</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Channel Performance */}
        <Card className="lg:col-span-8" data-testid="channel-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Kanal Performansı</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.total_calls > 0 || stats?.total_fallbacks > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={channelData} layout="vertical">
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={100} />
                    <Tooltip />
                    <Bar dataKey="success" fill="#0F172A" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Henüz iletişim verisi yok</p>
                  <p className="text-sm mt-1">Bir kampanya başlatarak istatistikleri görebilirsiniz</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card data-testid="quick-actions">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Hızlı İşlemler</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/contacts">
              <Button variant="outline" className="w-full justify-between h-auto py-4" data-testid="quick-action-contacts">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5" />
                  <div className="text-left">
                    <p className="font-medium">Kişi Ekle</p>
                    <p className="text-sm text-muted-foreground">CSV veya manuel</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            
            <Link to="/campaigns">
              <Button variant="outline" className="w-full justify-between h-auto py-4" data-testid="quick-action-campaigns">
                <div className="flex items-center gap-3">
                  <Megaphone className="w-5 h-5" />
                  <div className="text-left">
                    <p className="font-medium">Kampanya Oluştur</p>
                    <p className="text-sm text-muted-foreground">Yeni arama kampanyası</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            
            <Link to="/settings">
              <Button variant="outline" className="w-full justify-between h-auto py-4" data-testid="quick-action-settings">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5" />
                  <div className="text-left">
                    <p className="font-medium">Provider Ayarları</p>
                    <p className="text-sm text-muted-foreground">API anahtarları</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
